import { create } from 'zustand';
import lightsaberSound from '../assets/audio/lightsaber-sound.mp3';

type TileGroup = 'favorite' | 'standard';

interface TilesState {
  // Favorites
  favorites: string[];
  toggleFavorite: (route: string) => void;
  isFavorite: (route: string) => boolean;

  // Order
  favoritesOrder: string[];
  standardOrder: string[];
  updateOrder: (group: TileGroup, routes: string[]) => void;
  getOrderIndex: (group: TileGroup, route: string) => number;
}

const STORAGE_KEYS = {
  favorites: 'favorites',
  favoritesOrder: 'tile-order-favorites',
  standardOrder: 'tile-order-standard',
} as const;

const loadFromStorage = <T>(key: string, fallback: T): T => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
};

const saveToStorage = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
};

const isMayFourth = (): boolean => {
  const today = new Date();
  return today.getMonth() === 4 && today.getDate() === 4;
};

const playLightsaberSound = (): void => {
  const audio = new Audio(lightsaberSound);
  audio.play().catch(err => console.warn(err));
};

export const useTilesStore = create<TilesState>((set, get) => ({
  // Favorites state
  favorites: loadFromStorage<string[]>(STORAGE_KEYS.favorites, []),

  toggleFavorite: (route: string) => {
    if (isMayFourth()) {
      playLightsaberSound();
    }

    set(state => {
      const isFav = state.favorites.includes(route);
      const newFavorites = isFav
        ? state.favorites.filter(fav => fav !== route)
        : [...state.favorites, route];

      saveToStorage(STORAGE_KEYS.favorites, newFavorites);
      return { favorites: newFavorites };
    });
  },

  isFavorite: (route: string) => {
    return get().favorites.includes(route);
  },

  // Order state
  favoritesOrder: loadFromStorage<string[]>(STORAGE_KEYS.favoritesOrder, []),
  standardOrder: loadFromStorage<string[]>(STORAGE_KEYS.standardOrder, []),

  updateOrder: (group: TileGroup, routes: string[]) => {
    const key = group === 'favorite' ? STORAGE_KEYS.favoritesOrder : STORAGE_KEYS.standardOrder;
    const stateKey = group === 'favorite' ? 'favoritesOrder' : 'standardOrder';

    saveToStorage(key, routes);
    set({ [stateKey]: routes });
  },

  getOrderIndex: (group: TileGroup, route: string) => {
    const order = group === 'favorite' ? get().favoritesOrder : get().standardOrder;
    const index = order.indexOf(route);
    return index === -1 ? Infinity : index;
  },
}));
